import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Bot, ChevronDown, X, Lock, CreditCard } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../utils/designSystem'
import { AVAILABLE_AGENTS } from '../agents'
import { useAuth } from '../hooks/useSupabaseAuth'
import { useSubscription } from '../hooks/useSubscription'

const AgentSelector = ({ selectedAgent, onAgentSelect, onAgentClear }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useAuth()
  const { hasActiveSubscription, creditsRemaining, subscription } = useSubscription()

  const handleAgentSelect = (agent) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('openAuthModal'))
      return
    }

    // Simple check: allow selection regardless of subscription
    // Actual execution will be blocked in AgentPage if no active subscription

    onAgentSelect(agent)
    setIsOpen(false)
  }

  const getAgentStatus = (agent) => {
    if (!user) {
      return { accessible: false, hasCredits: false, needsLogin: true }
    }
    
    // Check if user has active subscription and sufficient credits
    const hasCredits = hasActiveSubscription && creditsRemaining > 0
    
    // For Novel Agent, check specific access
    if (agent.id === 'novel_agent') {
      // Novel Agent requires PRO subscription
      const packageName = subscription?.subscription_packages?.name
      const allowedAgents = subscription?.subscription_packages?.features?.allowed_agents || []
      const hasNovelAccess = allowedAgents.includes('novel')
      
      return {
        accessible: hasActiveSubscription && hasNovelAccess,
        hasCredits: hasCredits,
        needsLogin: false,
        packageName: packageName,
        needsUpgrade: !hasNovelAccess
      }
    }
    
    return {
      accessible: hasActiveSubscription,
      hasCredits: hasCredits,
      needsLogin: false
    }
  }

  const handleClearAgent = (e) => {
    e.stopPropagation()
    onAgentClear()
  }

  return (
    <div className="relative">
      {/* Agent Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm transition-colors rounded-lg w-full sm:w-auto",
          selectedAgent 
            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" 
            : "text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary"
        )}
      >
        <Bot className="w-3 h-3 sm:w-4 sm:h-4" />
        <span className="truncate">{selectedAgent ? selectedAgent.name : 'Pilih AI Agent'}</span>
        {selectedAgent ? (
          <button
            onClick={handleClearAgent}
            className="p-0.5 hover:bg-emerald-500/20 rounded transition-colors flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className={cn('w-3 h-3 transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && !selectedAgent && (
        <>
          <div className="absolute bottom-full left-0 right-0 sm:right-auto mb-1 border border-border-primary rounded-lg shadow-lg z-50 sm:w-80 max-h-[70vh] overflow-y-auto" style={{backgroundColor: '#121212'}}>
            <div className="p-1.5 sm:p-2">
              <div className="text-xs font-medium text-text-tertiary mb-2 px-2">AI Agents Tersedia</div>
              {AVAILABLE_AGENTS.map((agent) => {
                const status = getAgentStatus(agent)
                const isDisabled = !status.accessible || !status.hasCredits || status.needsLogin
                
                return (
                  <button
                    key={agent.id}
                    onClick={() => handleAgentSelect(agent)}
                    className={cn(
                      "w-full px-2 sm:px-3 py-2 sm:py-3 text-left transition-colors rounded-lg border-b border-border-secondary last:border-b-0",
                      isDisabled ? "opacity-60 cursor-not-allowed" : "hover:bg-bg-tertiary"
                    )}
                  >
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      <div 
                        className={cn(
                          "w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 relative",
                          isDisabled ? "opacity-50" : ""
                        )}
                        style={{ backgroundColor: `${agent.color}20`, color: agent.color }}
                      >
                        <agent.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                        {!status.accessible && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Lock className="w-2 h-2 text-white" />
                          </div>
                        )}
                        {status.accessible && !status.hasCredits && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                            <CreditCard className="w-2 h-2 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          "font-medium text-xs sm:text-sm flex items-center gap-2",
                          isDisabled ? "text-gray-500" : "text-text-primary"
                        )}>
                          {agent.name}
                          {status.needsLogin && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">
                              Login
                            </span>
                          )}
                          {!status.needsLogin && !status.accessible && (
                            <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                              Upgrade
                            </span>
                          )}
                          {!status.needsLogin && status.accessible && !status.hasCredits && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded">
                              Credits
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-text-tertiary mt-0.5 sm:mt-1 leading-relaxed line-clamp-2 sm:line-clamp-none">
                          {agent.description}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  )
}

// Agent Label Component for showing selected agent in input
const AgentLabel = ({ agent, onClear }) => {
  if (!agent) return null

  return (
    <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
      <div 
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm"
        style={{ backgroundColor: `${agent.color}15`, borderColor: `${agent.color}30` }}
      >
        <agent.icon className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: agent.color }} />
        <span className="font-medium truncate" style={{ color: agent.color }}>
          {agent.name}
        </span>
        <button
          onClick={onClear}
          className="p-0.5 hover:bg-black/10 rounded transition-colors"
          style={{ color: agent.color }}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

AgentSelector.propTypes = {
  selectedAgent: PropTypes.object,
  onAgentSelect: PropTypes.func.isRequired,
  onAgentClear: PropTypes.func.isRequired
}

AgentLabel.propTypes = {
  agent: PropTypes.object,
  onClear: PropTypes.func.isRequired
}

export default AgentSelector
export { AgentLabel }